import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Progress } from '../components/Progress';
import { Users, FileText, AlertTriangle, Activity, MapPin, ShieldAlert, Crosshair, TrendingUp } from 'lucide-react';
import { KPICard } from '../components/KPICard';
import { StatusBadge } from '../components/StatusBadge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { NASIONAL, TREN_7_HARI, TOP_PRIORITAS } from '../data/binda';
import { PetaOperasiNasional } from '../components/PetaOperasiNasional';
import { useNavigate } from 'react-router-dom';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { SyncButton } from '../components/SyncButton';
import { useStore, isOfficialProduct } from '../data/store';
export default function NationalDashboard() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filteredPrioritas = TOP_PRIORITAS.filter(region => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || region.provinsi.toLowerCase().includes(search) || region.kode.toLowerCase().includes(search);
    const matchesStatus = !filterValues.status || filterValues.status === 'all' || region.status === filterValues.status;
    return matchesSearch && matchesStatus;
  });
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Provinsi/BINDA',
    type: 'search',
    placeholder: 'Cari provinsi...'
  }, {
    key: 'date',
    label: 'Tanggal',
    type: 'datePreset'
  }, {
    key: 'status',
    label: 'Status Wilayah',
    type: 'statusBadge',
    options: [{
      label: 'Kritis',
      value: 'Kritis'
    }, {
      label: 'Waspada',
      value: 'Waspada'
    }, {
      label: 'Perhatian',
      value: 'Perhatian'
    }, {
      label: 'Normal',
      value: 'Normal'
    }]
  }, {
    key: 'category',
    label: 'Kategori Laporan',
    type: 'select',
    options: [{
      label: 'Politik',
      value: 'Politik'
    }, {
      label: 'Ekonomi',
      value: 'Ekonomi'
    }, {
      label: 'Keamanan',
      value: 'Keamanan'
    }]
  }, {
    key: 'urgency',
    label: 'Tingkat Urgensi',
    type: 'urgencyBadge',
    options: [{
      label: 'Rendah',
      value: 'RENDAH'
    }, {
      label: 'Menengah',
      value: 'MENENGAH'
    }, {
      label: 'Tinggi',
      value: 'TINGGI'
    }, {
      label: 'Kritis',
      value: 'KRITIS'
    }]
  }];
  const exportColumns = [{
    key: 'kode',
    label: 'Kode'
  }, {
    key: 'provinsi',
    label: 'Provinsi'
  }, {
    key: 'status',
    label: 'Status'
  }, {
    key: 'laporanHariIni',
    label: 'Laporan Hari Ini'
  }, {
    key: 'personelAktif',
    label: 'Personel Aktif'
  }];
  const reportsList = useStore(s => s.reports);
  const escalatedReports = reportsList.filter(isOfficialProduct);
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2490">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-2491">
        <div data-id="element-2492">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-2493">
            DASHBOARD MARKAS BESAR NASIONAL
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-2494">
            Pusat Operasi Strategis // Tinjauan Nasional
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-2495">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-2496">
            <Filter className="w-4 h-4 mr-2" data-id="element-2497" />
            Filter
          </Button>
          <SyncButton data-id="element-2498" />
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredPrioritas.length} exportData={filteredPrioritas} exportColumns={exportColumns} exportFilename="Top_Prioritas_Nasional" data-id="element-2499" />}

      {/* KPI Cards - High Density */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-2500">
        <KPICard title="Personel Aktif" value={NASIONAL.totalPersonelAktif.toLocaleString('id-ID')} icon={Users} colorVar="var(--primary)" progress={{
        value: 92,
        label: 'Tingkat Penugasan',
        target: '92%'
      }} onClick={() => navigate('/personnel')} data-id="element-2501" />
        <KPICard title="Laporan Hari Ini" value={NASIONAL.laporanHariIni.toLocaleString('id-ID')} icon={FileText} colorVar="var(--status-info)" trend={{
        value: '+12.4%',
        label: 'vs kemarin',
        isPositive: true
      }} onClick={() => navigate('/reports')} data-id="element-2502" />
        <KPICard title="Laporan Prioritas" value={NASIONAL.laporanPrioritas} icon={ShieldAlert} colorVar="var(--destructive)" trend={{
        value: 'TINDAKAN DIPERLUKAN',
        label: '',
        isPositive: false
      }} onClick={() => navigate('/reports')} data-id="element-2503" />
        <KPICard title="KPI Nasional" value={NASIONAL.kpiNasional} icon={Crosshair} colorVar="var(--status-active)" progress={{
        value: NASIONAL.kpiNasional,
        label: 'Kategori A - Sangat Baik',
        target: 'Target: 85'
      }} onClick={() => navigate('/kpi')} data-id="element-2504" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-id="element-2505">
        {/* Advanced GIS Map */}
        <Card className="xl:col-span-2 glass-panel overflow-hidden border-border/50 flex flex-col" data-id="element-2506">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-2507">
            <div className="flex justify-between items-center" data-id="element-2508">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2509">
                <MapPin className="w-4 h-4 text-primary" data-id="element-2510" />
                Peta Operasi Nasional
              </CardTitle>
              <div className="flex gap-2" data-id="element-2511">
                <Badge variant="outline" className="text-[10px] font-mono bg-background/50" data-id="element-2512">
                  SAT: ONLINE
                </Badge>
                <Badge variant="outline" className="text-[10px] font-mono bg-background/50" data-id="element-2513">
                  SIGINT: AKTIF
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 min-h-[440px]" data-id="element-2514">
            <PetaOperasiNasional height={440} data-id="element-2515" />
          </CardContent>
        </Card>

        {/* Regional Status Matrix */}
        <Card className="glass-panel border-border/50 flex flex-col" data-id="element-2516">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-2517">
            <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-2518">
              Top 10 Wilayah Prioritas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-2519">
            {filteredPrioritas.length === 0 ? <EmptyState icon={Filter} title="TIDAK ADA WILAYAH" description="Tidak ada wilayah yang sesuai dengan filter saat ini." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none h-full" data-id="element-2520" /> : <div className="divide-y divide-border/50" data-id="element-2521">
                {filteredPrioritas.map((region, i) => <div key={i} className="p-4 hover:bg-secondary/30 transition-colors flex items-center justify-between group cursor-pointer" onClick={() => navigate(`/regional/${region.kode}`)} data-id="element-2522">
                    <div data-id="element-2523">
                      <div className="font-heading font-semibold text-sm group-hover:text-primary transition-colors" data-id="element-2524">
                        {region.provinsi}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground mt-1 flex gap-3" data-id="element-2525">
                        <span data-id="element-2526">LAP: {region.laporanHariIni}</span>
                        <span data-id="element-2527">PRS: {region.personelAktif}</span>
                      </div>
                    </div>
                    <StatusBadge status={region.status} data-id="element-2528" />
                  </div>)}
              </div>}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2529">
        <Card className="glass-panel border-border/50 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => navigate('/reports')} data-id="element-2530">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-2531">
            <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-2532">
              Volume Intelijen (7 Hari)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-id="element-2533">
            <div className="h-[250px] w-full" data-id="element-2534">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2535">
                <AreaChart data={TREN_7_HARI} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2536">
                  <defs data-id="element-2537">
                    <linearGradient id="colorReports" x1="0" y1="0" x2="0" y2="1" data-id="element-2538">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} data-id="element-2539" />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} data-id="element-2540" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} opacity={0.5} data-id="element-2541" />
                  <XAxis dataKey="hari" stroke="var(--muted-foreground)" fontSize={10} tickLine={false} axisLine={false} fontFamily="monospace" data-id="element-2542" />
                  <YAxis stroke="var(--muted-foreground)" fontSize={10} tickLine={false} axisLine={false} fontFamily="monospace" data-id="element-2543" />
                  <Tooltip contentStyle={{
                  backgroundColor: 'rgba(15, 18, 25, 0.9)',
                  borderColor: 'var(--border)',
                  borderRadius: '4px',
                  backdropFilter: 'blur(8px)'
                }} itemStyle={{
                  color: 'var(--foreground)',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }} labelStyle={{
                  fontFamily: 'monospace',
                  fontSize: '10px',
                  color: 'var(--muted-foreground)'
                }} data-id="element-2544" />
                  <Area type="monotone" dataKey="laporan" name="Total Laporan" stroke="var(--primary)" fillOpacity={1} fill="url(#colorReports)" strokeWidth={2} data-id="element-2545" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-border/50 flex flex-col" data-id="element-2546">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-2547">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-destructive flex items-center gap-2" data-id="element-2548">
              <ShieldAlert className="w-4 h-4" data-id="element-2549" /> Laporan Eskalasi Nasional
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/reports')} data-id="element-2550">
              Lihat Semua
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto max-h-[300px]" data-id="element-2551">
            <div className="divide-y divide-border/50" data-id="element-2552">
              {escalatedReports.length > 0 ? escalatedReports.map(report => <div key={report.id} className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${report.id}`)} data-id="element-2553">
                    <div className="flex justify-between items-start mb-2" data-id="element-2554">
                      <span className="font-mono text-xs text-primary" data-id="element-2555">
                        {report.id}
                      </span>
                      <Badge variant="destructive" className="text-[10px]" data-id="element-2556">
                        ESKALASI
                      </Badge>
                    </div>
                    <div className="font-heading font-semibold text-sm mb-2" data-id="element-2557">
                      {report.title}
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-mono text-muted-foreground" data-id="element-2558">
                      <span className="flex items-center gap-1" data-id="element-2559">
                        <MapPin className="w-3 h-3" data-id="element-2560" /> {report.region}
                      </span>
                      <span data-id="element-2561">{report.time}</span>
                    </div>
                  </div>) : <div className="p-8 text-center text-muted-foreground font-mono text-xs" data-id="element-2562">
                  Tidak ada laporan eskalasi nasional.
                </div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}